import React, { CSSProperties } from "react"
import { colors, fonts } from "../Styles"

// Buttonstyles (CSS) used: 
// ...css.button_FitContent
// ...css.button_TextUnderline

export enum ButtonTypes {
  primaryButton ="primaryButton",
  textButton = "textButton",
  tableButton = "tableButton",
  tabButton = "tabButton",
  globalMenuButton = "globalMenuButton",
  cardButton = "cardButton",
  glyphButton = "glyphButton",
  arrowButton = "arrowButton",
  tagButton = "tagButton"
}

export interface Button_Props_overrides {
  baseStyles?: CSSProperties;
  hoverStyles?: CSSProperties;
  startEnhancerStyles?: CSSProperties;
  endEnhancerStyles?: CSSProperties;
  hoverEnhancerStyles?: CSSProperties;
  contentStyles?: CSSProperties;
  subContentStyles?: CSSProperties;
  hoverSubContentStyles?: CSSProperties;
  disabledStyles?: CSSProperties;
}

export interface StyledButtonProps {
  $buttonType?: ButtonTypes;
  $focus_view?: boolean;
  $hasstartenhancer?: boolean;
  $hasEndEnhancer?: boolean;
  $overrides?: Button_Props_overrides;
}

export interface CreateButtonStyleProps {
  buttonType?: ButtonTypes;
  focus_view?: boolean;
  hasstartenhancer?: boolean;
  hasEndEnhancer?: boolean;
  overrides?: Button_Props_overrides;
}

export const CreateButtonStyle = ( { buttonType, focus_view, hasstartenhancer, hasEndEnhancer, overrides }: CreateButtonStyleProps ) => {
  const merge = (defaultStyles: CSSProperties, overrideStyles?: CSSProperties) => ({ ...defaultStyles, ...(overrideStyles || {})});
  
  let baseStyles = {} as CSSProperties
  let hoverStyles = {} as CSSProperties
  let startEnhancerStyles = {} as CSSProperties
  let endEnhancerStyles = {} as CSSProperties
  let contentStyles = {} as CSSProperties
  let subContentStyles = {} as CSSProperties
  let hoverSubContentStyles = {} as CSSProperties
  let hoverEnhancerStyles = {} as CSSProperties
  let disabledStyles = {} as CSSProperties

  switch (buttonType) {
    case ButtonTypes.textButton:
      baseStyles = { 
        width: "fit-content",
        height: "fit-content",
        backgroundColor: "transparent",
        padding: "0px",
        display: "grid",
        gridTemplateColumns: hasstartenhancer && hasEndEnhancer ? "auto 1fr auto" : hasstartenhancer ? "auto 1fr" : hasEndEnhancer ? "1fr auto" : "1fr",
        alignItems: "center",
        gap: hasstartenhancer && hasEndEnhancer ? "5px" : hasstartenhancer ? "5px" : hasEndEnhancer ? "5px" : undefined,
      }
      hoverStyles = { 
        opacity: "0.5",
        transition: "0.2s ease"
      }
      startEnhancerStyles = {
        gridArea: "1/1/2/2",
        height: "20px",
        pointerEvents: 'none',
      }
      endEnhancerStyles = {
        gridArea: "1/3/2/4",
        height: "20px",
        pointerEvents: 'none',
      }
      contentStyles = {
        gridArea: hasstartenhancer && hasEndEnhancer ? "1/2/2/3" : hasstartenhancer ? "1/2/2/3" : hasEndEnhancer ? "1/1/2/2" : "1/1/2/2",
        whiteSpace: "nowrap",
        textAlign: "center",
        pointerEvents: 'none',
        color: colors.greyLightTertiary,
        ...fonts.header,
        ...(focus_view ? fonts.fontSize2 : fonts.fontSize3),
      }
    break
    case ButtonTypes.tableButton:
      baseStyles = { 
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "fit-content",
        minWidth: "50px",
        height: "50px",
        justifyContent: "end",
        padding: "0px 5px 5px 5px",
        borderRadius: "8px 8px 0px 0px",
        backgroundColor: colors.greySecondary,
        gap: "3px"
      }
      hoverStyles = {
        transition: "0.2s ease",
        backgroundColor: colors.greyPrimary
      }
      contentStyles = {
        ...fonts.header,
        ...(focus_view ? fonts.fontSize3 : fonts.fontSize4),
        color: colors.greyLightTertiary,
        pointerEvents: 'none',
      }
      startEnhancerStyles = {
        pointerEvents: 'none',
        // margin: "0px 0px 5px 0px"
      }
    break
    case ButtonTypes.tabButton:
      baseStyles = {
        width: "fit-content",
        height: "40px",
        backgroundColor: colors.greyDarkPrimary,
        justifyContent: "center",
        borderRadius: "8px 8px 0px 0px",
        color: colors.greyLightTertiary,
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        padding: "0px 10px",
      }
      hoverStyles = {
        backgroundColor: colors.blueDarkPrimary,
        height: "45px",
        paddingTop: "0px"
      }
      contentStyles = {
        ...fonts.header,
        ...fonts.textShadow,
        ...fonts.fontSize4,
        pointerEvents: 'none',
      }
    break
    case ButtonTypes.globalMenuButton:
      baseStyles = {
        width: "130px",
        height: "80px",
        backgroundColor: colors.greyDarkPrimary,
        gap: "5px",
        padding: "0px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "8px",
        color: colors.greyLightTertiary
      }
      hoverStyles = {
        backgroundColor: colors.blueDarkPrimary
      }
      startEnhancerStyles = {
        margin: "0px",
        pointerEvents: 'none',
      }
      endEnhancerStyles = {
        margin: "0px",
        pointerEvents: 'none',
      }
      hoverEnhancerStyles = {
        filter: "brightness(1.5)"
      }
      contentStyles = {
        color: colors.greyLightTertiary,
        ...fonts.header,
        ...fonts.textShadow, 
        ...fonts.fontSize4,
        textAlign: "center",
        pointerEvents: 'none',
        }
    break
    case ButtonTypes.cardButton:
      baseStyles = {
        cursor: "pointer", 
        minWidth: "320px", 
        width: "100%",
        height: "200px",  
        backgroundColor: colors.greyPrimary, 
        border: "5px solid "+colors.greyPrimary,
        borderRadius: "10px", 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center",
        transition: '0.3s',
        filter: 'opacity(1)',
        boxSizing: "border-box"
      }
      hoverStyles = {
        filter: 'opacity(0.3)', 
        backgroundColor: colors.greyPrimary
      }
      contentStyles = {
        ...fonts.header,
        color: colors.greyLightTertiary,
        ...fonts.fontSize3,
        pointerEvents: 'none',
      }
      subContentStyles = {
        color: colors.greyLightTertiary,
        ...fonts.fontSize4,
        ...fonts.paragraph,
        pointerEvents: 'none',
      }
      break
    case ButtonTypes.glyphButton:
      baseStyles = {
        width: "fit-content",
        height: "fit-content",
        backgroundColor: colors.transparent,
        padding: "0px",
      }
      hoverStyles = {
        backgroundColor: colors.transparent,
                opacity: "0.5",
      }
      startEnhancerStyles = {
        height: "20px",
        pointerEvents: 'none',
      }
      disabledStyles = {
        opacity: "0.3"
      }
      break;
      case ButtonTypes.arrowButton:
        baseStyles = {
          width: "35px",
          height: "30px",
          backgroundColor: colors.greyDarkPrimary,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "8px"
        }
        hoverStyles = {
          color: colors.greyLightTertiary + "!important",
          backgroundColor: colors.blackPrimary
        }
        startEnhancerStyles = {
          height: "20px",
          pointerEvents: 'none',
        }
        disabledStyles = {
          opacity: "0.3"
        }
      break;
      case ButtonTypes.tagButton:
        baseStyles = { 
          width: "fit-content",
          height: "fit-content",
          backgroundColor: colors.greyPrimary,
          padding: "4px 6px",
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: hasEndEnhancer ? "1fr auto" : "1fr",
          alignItems: "center",
          gap: hasEndEnhancer ? "5px" : undefined,
        }
        hoverStyles = { 
          backgroundColor: colors.greySecondary,
        }
        endEnhancerStyles = {
          pointerEvents: 'none',
          gridArea: "1/2/2/3",
          height: "14px",
        }
        contentStyles = {
          pointerEvents: 'none',
          gridArea: "1/1/2/2",
          whiteSpace: "nowrap",
          textAlign: "center",
          color: colors.greyLightTertiary,
          ...fonts.header,
          ...(focus_view ? fonts.fontSize3 : fonts.fontSize4),
        }
      break
    case ButtonTypes.primaryButton:
    default:   
      baseStyles = { 
        position: "relative",
        width: focus_view ? "80vw" : "270px",
        height: focus_view ? "60px" : "50px",
        backgroundColor: colors.greyDarkPrimary,
        borderRadius: "8px",
        display: "grid",
        gridTemplateColumns: hasstartenhancer && hasEndEnhancer ? "auto 1fr auto" : hasstartenhancer ? "auto 1fr" : hasEndEnhancer ? "1fr auto" : "1fr",
        gap: hasstartenhancer || hasEndEnhancer ? "10px" : "0px",
        alignItems: "center",
        justifyContent: "center",
        padding: "0px 10px"
      }
      hoverStyles = { 
        color: colors.greyLightTertiary+"!important",
        backgroundColor: colors.blackPrimary 
      }
      contentStyles = {
        pointerEvents: 'none',
        gridArea: hasstartenhancer && hasEndEnhancer ? "1/2/2/3" : hasstartenhancer ? "1/2/2/3" : hasEndEnhancer ? "1/1/2/2" : "1/1/2/2",
        display: "flex",
        justifyContent: hasstartenhancer || hasEndEnhancer ? "start" : "center",
        color: colors.greyLightTertiary,
        whiteSpace: "nowrap",
        ...fonts.header,
        ...fonts.textShadow, 
        ...(focus_view ? fonts.fontSize3 : fonts.fontSize4),
      }
      startEnhancerStyles = {
        pointerEvents: 'none',
        gridArea: "1/1/2/2",
        height: focus_view ? "40px" : "35px"
      }
      endEnhancerStyles = {
        pointerEvents: 'none',
        gridArea: "1/3/2/4",
        height: focus_view ? "40px" : "35px"
      }
      subContentStyles = {
        pointerEvents: 'none'
      }
    break
  }
  return {
    baseStyles: merge(baseStyles, overrides?.baseStyles),
    hoverStyles: merge(hoverStyles, overrides?.hoverStyles),
    startEnhancerStyles: merge(startEnhancerStyles, overrides?.startEnhancerStyles),
    endEnhancerStyles: merge(endEnhancerStyles, overrides?.endEnhancerStyles),
    contentStyles: merge(contentStyles, overrides?.contentStyles),
    subContentStyles: merge(subContentStyles, overrides?.subContentStyles),
    hoverSubContentStyles: merge(hoverSubContentStyles, overrides?.hoverSubContentStyles),
    hoverEnhancerStyles: merge(hoverEnhancerStyles, overrides?.hoverEnhancerStyles),
    disabledStyles: merge(disabledStyles, overrides?.disabledStyles)
  }
}